import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { X } from "lucide-react";
import { healthRecommendationAPI } from "../services/api";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background-color: #FFFFFF;
  border-radius: 1rem;
  padding: 0;
  width: 90vw;
  max-width: 800px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  transform: ${props => props.isOpen ? 'scale(1)' : 'scale(0.95)'};
  transition: transform 0.3s ease;
  font-family: 'Prompt', sans-serif;
`;

const ModalHeader = styled.div`
  padding: 2rem 2rem 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ECECEC;
`;

const ModalTitle = styled.h2`
  color: #000000;
  font-size: 25px;
  font-weight: 400;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  svg {
    color: #CFD3D4;
    width: 11px;
    height: 11px;
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 400;
  color: #6F6F6F;
  margin: 0 0 1rem 0;
  text-align: center;
`;

const PatientInfoSection = styled.div`
  margin-bottom: 1rem;
`;

const PatientInfoContainer = styled.div`
  text-align: center;
  margin-bottom: 0.5rem;
`;

const PatientInfoLine = styled.div`
  font-size: 16px;
  color: #6F6F6F;
  margin-bottom: 0.25rem;
`;

const DiseaseInfo = styled.div`
  font-size: 16px;
  color: #6F6F6F;
  text-align: center;
`;

const InfoItem = styled.div`
  font-size: 16px;
  color: #6F6F6F;
`;

const InfoLabel = styled.span`
  font-weight: 400;
`;

const InfoValue = styled.span`
  font-weight: 700;
`;

const HealthResultSection = styled.div`
  margin-bottom: 1rem;
`;

const TemplateContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
  justify-content: center;
`;

const TemplateTag = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: transparent;
  border: 1px solid #00B5B8;
  border-radius: 0px;
  padding: 4px 8px;
  margin: 2px;
  font-size: 14px;
  color: #00B5B8;
  cursor: pointer;
  position: relative;
  width: fit-content;

  &:hover {
    background-color: #00B5B8;
    color: white;
  }
`;

const AIGeneratingSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  margin: 1rem 0;
`;

const AIImage = styled.img.attrs(props => ({
  // กรอง props ที่ไม่ควรส่งไปยัง DOM
  isGenerating: undefined
}))`
  width: 82px;
  height: 103px;
  object-fit: cover;
  margin-bottom: 0.5rem;
  animation: ${props => props.$isGenerating ? 'pulse 2s infinite' : 'none'};

  @keyframes pulse {
    0% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.05); opacity: 0.8; }
    100% { transform: scale(1); opacity: 1; }
  }
`;

const GeneratingText = styled.p`
  font-size: 16px;
  color: #6F6F6F;
  text-align: center;
  margin: 0;
`;

const ErrorContainer = styled.div`
  background-color: #FEE2E2;
  border: 1px solid #FCA5A5;
  border-radius: 0.5rem;
  padding: 1rem;
  margin: 1rem 0;
  text-align: center;
`;

const ErrorText = styled.p`
  color: #DC2626;
  font-size: 14px;
  margin: 0 0 0.5rem 0;
`;

const RetryButton = styled.button`
  background-color: #DC2626;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #B91C1C;
  }
`;

const ModalFooter = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid #ECECEC;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const FinishButton = styled.button`
  background-color: ${props => props.disabled ? '#CFD3D4' : '#00B5B8'};
  color: #FFFFFF;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 2rem;
  font-size: 14px;
  font-weight: 400;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s;

  &:hover {
    background-color: ${props => props.disabled ? '#CFD3D4' : '#0891B2'};
  }
`;

const AIRecommendationModal = ({ isOpen, onClose, patientData, onFinish }) => {
  const [isGenerating, setIsGenerating] = useState(true);
  const [recommendations, setRecommendations] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && patientData?.id) {
      generateRecommendations();
    }
  }, [isOpen, patientData]);

  const generateRecommendations = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      // เรียก API เพื่อ generate คำแนะนำจาก OpenAI
      const response = await healthRecommendationAPI.generateRecommendations(patientData.id);

      if (response.status === 'success') {
        setRecommendations(response.data.recommendations);
      } else {
        setError(response.message || 'เกิดข้อผิดพลาดในการสร้างคำแนะนำ');
      }
    } catch (err) {
      console.error('Error generating recommendations:', err);
      setError('ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาลองใหม่อีกครั้ง');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose && onClose();
    }
  };

  const handleFinish = () => {
    if (!isGenerating && recommendations) {
      // ส่งข้อมูล AI recommendations กลับไปยัง parent component
      if (onFinish) {
        onFinish(recommendations);
      }
      onClose && onClose();
    }
  };

  if (!isOpen || !patientData) return null;

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContent isOpen={isOpen}>
        <ModalHeader>
          <ModalTitle>
            รับคำแนะนำจากโปรแกรมอัตโนมัติ
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {/* Patient Information Section */}
          <PatientInfoSection>
            <PatientInfoContainer>
              <PatientInfoLine>
                ผลสุขภาพ คุณ <InfoValue>{patientData.patientName}</InfoValue> เพศ : <InfoValue>{patientData.gender}</InfoValue> อายุ : <InfoValue>{patientData.age}</InfoValue>
              </PatientInfoLine>
              <DiseaseInfo>
                โรคประจำตัว : <InfoValue>{patientData.diseases}</InfoValue>
              </DiseaseInfo>
            </PatientInfoContainer>
          </PatientInfoSection>

          {/* Health Result Section */}
          <HealthResultSection>
            <SectionTitle>แปลผลสุขภาพ</SectionTitle>
            <TemplateContainer>
              {patientData.templates?.map((template, index) => (
                <TemplateTag key={index}>
                  {template}
                </TemplateTag>
              ))}
            </TemplateContainer>
          </HealthResultSection>

          {/* AI Generating Section */}
          <AIGeneratingSection>
            <AIImage
              $isGenerating={isGenerating}
              src="Master_Line.png"
              alt="AI Generation"
            />
            <GeneratingText>
              {isGenerating ? 'กำลังสร้างคำแนะนำจาก AI...' : error ? 'เกิดข้อผิดพลาด' : 'สร้างคำแนะนำเสร็จสิ้น'}
            </GeneratingText>
          </AIGeneratingSection>

          {/* Error Section */}
          {error && (
            <ErrorContainer>
              <ErrorText>{error}</ErrorText>
              <RetryButton onClick={generateRecommendations}>
                ลองใหม่อีกครั้ง
              </RetryButton>
            </ErrorContainer>
          )}

        </ModalBody>

        <ModalFooter>
          <FinishButton
            disabled={isGenerating || error}
            onClick={handleFinish}
          >
            เสร็จสิ้น
          </FinishButton>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AIRecommendationModal;